/**
 * The one zustand store (DESIGN-client §1.1). Slices: `session`, `table`, `presentation`,
 * `settings` (+ `privateView`, the per-seat private payload, which the design's slice list
 * omits but the hand/HUD need).
 *
 * Rules: React never touches Pixi; Pixi never imports React. Pixi reads this store via
 * `store.subscribe(selector, cb)` (subscribeWithSelector) for layout mode, perf tier, motion,
 * mutedEmotes, participants. Eventful things reach the scene only via `CueQueue`.
 *
 * `settings` persists to localStorage under `sn.settings` behind a try/catch storage shim so
 * private windows / blocked storage never break boot.
 */
import { useStore as useZustandStore } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import type { ProfileView, ProgressMessage } from '@spades/shared';
import type { AuthNotice } from '../bridge/identity';
import type { CueData, Participant, Player, SeatIndex, SeatView, TableView } from '../net/types-local';

export type Platform = 'discord' | 'web';
export type Screen = 'boot' | 'lobby' | 'table' | 'handEnd' | 'matchEnd' | 'tutorial';
export type LayoutMode = 'focused' | 'pip' | 'grid';
export type Orientation = 'portrait' | 'landscape';
export type PerfTier = 0 | 1 | 2 | 3;
export type MotionPref = 'full' | 'reduced' | 'system';
export type Takeover = 'set' | 'boston' | null;
/**
 * `joinFailed` is deliberately not a flavour of `lost`. The socket was fine and the server answered;
 * the answer was "no such table". Folding it into `lost` made the shell say "Connection lost" and
 * offer a Try again that could not retry the join, because it had no code to retry with.
 */
export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'lost' | 'joinFailed';

/** Why a table code did not work: never sent because it was not a code, or sent and refused. */
export type JoinFailureKind = 'malformed' | 'unknown';

export interface SessionSlice {
  me: Player | null;
  seat: SeatIndex | null;
  platform: Platform;
  /** Server-issued JWT (also in localStorage `sn.token`); null until identity resolves. */
  token: string | null;
  participants: Participant[];
  speaking: Record<string, boolean>;
  muted: Record<string, boolean>;
  connection: ConnectionState;
  /**
   * Why the connection ended, when the server said. Currently only set when this player opened the
   * table in another tab, so the shell can say "Playing in another tab" instead of "Reconnecting".
   */
  connectionReason: string | null;
  /**
   * The code that failed and why, set alongside `connection: 'joinFailed'`. The banner needs both
   * to say which code it was, so they travel together rather than as a loose string.
   */
  joinFailure: { code: string; kind: JoinFailureKind } | null;
  /**
   * The last server refusal of an intent this client sent, or null before the first one.
   *
   * The scene is optimistic: a bid is committed locally the moment it is dragged, a card leaves the
   * hand the moment it is played. When the server says no, something has to put the surface back,
   * and until this existed the refusal only reached a `console.warn` — so a player whose bid was
   * refused sat on a terminal local stack and could not bid again for the rest of the hand (client
   * review #2). `intent` is what separates the repairs: `bad_phase` answering a bid re-offers the
   * stack, the same code answering a play bounces the card.
   *
   * `at` is `Date.now()` at receipt, and it is the field a subscriber should key on: two identical
   * refusals in a row are two events, and comparing `code` alone would swallow the second.
   * Transport errors never land here — only the room's own `error {code,msg,intent}` messages.
   */
  lastIntentError: { code: string; msg: string; intent: string | null; at: number } | null;
  /** Boot/identity error, if any (shown by the Boot screen). */
  error: string | null;
  /**
   * What a sign-in link or the Discord callback did on this load (web only). Set by boot, toasted
   * once by the shell and cleared, so a remount never says it twice.
   */
  authNotice: AuthNotice | null;
}

export interface PresentationSlice {
  screen: Screen;
  layoutMode: LayoutMode;
  orientation: Orientation;
  perfTier: PerfTier;
  cueBusy: boolean;
  subtitle: { text: string; until: number } | null;
  takeover: Takeover;
  anchors: Record<string, DOMRect>;
  displayedScores: [number, number];
  slamCharge: number;
  /** Boot asset progress 0..1 (Boot screen). */
  bootProgress: number;
  /**
   * HUD-safe insets published by the scene layout (CSS px); null → safe-area env() only.
   *
   * `corner` is `bottom` asked only of the bottom-RIGHT corner, which a landscape card fan never
   * reaches. The time bank uses it so it can sit in the corner instead of floating 280px up the
   * side of the screen to clear cards that are nowhere near it.
   */
  hudSafe: { top: number; bottom: number; left: number; right: number; corner: number } | null;
  /** Data of the last `handScored` cue (HandEnd screen). */
  lastHandResult: CueData['handScored'] | null;
  /** Data of the last `matchEnd` cue (MatchEnd screen). */
  lastMatchResult: CueData['matchEnd'] | null;
  // --- shell agent (announcer) ---------------------------------------------------------------
  /**
   * ElevenLabs voice id whose clip pack loaded successfully (`shell/announcerManifest.ts`);
   * null means no voice pack is installed and the announcer stays subtitle-only.
   */
  announcerVoiceId: string | null;
  /**
   * Set while the `dealt` cue offered this seat a blind nil; null once the player bids or peeks.
   * `sealed` is true while the hand is still face-down under the wax band, so the DOM prompt can
   * offer both choices. Written by the scene's bid cue handlers (C-T15) — `scene/**` cannot reach
   * the store itself — and read by `shell/overlays/BlindNilPrompt.tsx`.
   */
  blindNilOffer: { sealed: boolean } | null;
  /**
   * True while the bid set piece owns the screen (scene4).
   *
   * The HUD's extras — the time bank, the rules and settings buttons, the emote picker — are for a
   * table in play. During the bid they are clutter around the one thing the player is being asked
   * to do, so the shell stands them down while this is set. The scores and the hand counter stay:
   * a bid is made against the score.
   */
  bidLive: boolean;
  /**
   * The bid the player has picked and could send, or null (scene4).
   *
   * `shell/overlays/BidConfirm.tsx` renders the gold Confirm button off this, positioned on the
   * `bid.commit` anchor. The scene decides whether there is a bid to confirm and what it is; the
   * shell owns the button, because the shell owns what a gold primary button looks like. `nil` is
   * a value of zero, which is not "no bid" — it is the dare, and the button says so.
   *
   * `commit` rides along rather than living in a module handle of its own. It did live in one, and
   * under React's double-mount the scene's disposer nulled it while the button was still on screen:
   * clicking did nothing, and only the keyboard route worked, because that is the one path that
   * does not go through it. A button that exists can now always send the bid it is showing.
   */
  bidConfirm: { value: number; nil: boolean; commit: () => void } | null;
  /**
   * The emote picker is open (§7).
   *
   * It used to be local React state behind a 💬 button in the HUD's bottom cluster. The button is
   * gone: the affordance is the player's own avatar, which is Pixi, which cannot reach React — so
   * the open flag had to become state both sides can see. `SeatView` → `SeatsLayer` → `TableScene`
   * → `TablePorts.openEmotes` → here, and `Hud` renders `EmotePicker` from it.
   */
  emotePickerOpen: boolean;
  /**
   * The table has actually painted, published by the scene's `publishPainted` port.
   *
   * Not "the scene installed" and not "the assets arrived": both of those are true a second or more
   * before the felt has anything on it, which is how a cold production load spent two seconds
   * showing a complete HUD over an empty canvas. The scene waits for its dressing to decode, for
   * real state to arrive, and then for one more ticker frame — Pixi runs the ticker before the
   * renderer, so declaring it on the ready frame would still be a frame early.
   */
  scenePainted: boolean;
  // --- end shell agent -----------------------------------------------------------------------
}

export interface SettingsSlice {
  volume: { master: number; sfx: number; music: number; announcer: number };
  /**
   * Master mute, the speaker button in the entry and lobby headers. Separate from `volume.master`
   * so unmuting brings back the level the player had. Not the Discord voice mute (`session.muted`).
   */
  audioMuted: boolean;
  motion: MotionPref;
  perfOverride: PerfTier | 'auto';
  subtitles: boolean;
  mutedEmotes: string[];
  tutorialDone: boolean;
  emoteSlots: string[];
}

/**
 * Lifetime progression: who the player is on the ladder, and what the match that just ended paid.
 *
 * Deliberately two separate things rather than one:
 *
 *  - `me` is a SNAPSHOT of the profile route, fetched when a surface asks for it (the Profile
 *    sheet) and never kept in sync — nothing else in the app needs it, and polling a record that
 *    only changes when a match ends would be noise on every table.
 *  - `lastMatch` is the one-shot `progress` message the room sends at match end. It is the XP
 *    panel's whole input, and it is cleared when the next match starts (`setTable` below), so a
 *    rematch that the server could not pay — records offline, say — shows no panel rather than
 *    the previous match's ledger a second time.
 *
 * XP never travels in room state, only in that message. A schema field would be re-sent on every
 * reconnect and the panel would re-animate a level-up the player already watched.
 */
export interface ProgressSlice {
  me: ProfileView | null;
  lastMatch: ProgressMessage | null;
  /** A profile fetch is in flight. Owned by whoever called `fetchProfile`. */
  loading: boolean;
}

export interface RootState {
  session: SessionSlice;
  /** Authoritative public mirror (schema → plain object). */
  table: TableView | null;
  /** Private per-seat view from the `private` message; null for spectators. */
  privateView: SeatView | null;
  presentation: PresentationSlice;
  settings: SettingsSlice;
  progress: ProgressSlice;
}

export interface StoreActions {
  setSession(patch: Partial<SessionSlice>): void;
  setSpeaking(userId: string, speaking: boolean): void;
  setMuted(userId: string, muted: boolean): void;
  setTable(view: TableView | null): void;
  setPrivateView(view: SeatView | null): void;
  setPresentation(patch: Partial<PresentationSlice>): void;
  setAnchor(name: string, rect: DOMRect): void;
  setSettings(patch: Partial<SettingsSlice>): void;
  setVolume(patch: Partial<SettingsSlice['volume']>): void;
  /** The loaded profile, or null with `loading` to arm a spinner. */
  setProfile(profile: ProfileView | null, loading?: boolean): void;
  /** The room's one-shot `progress` message. Called from the driver glue at match end. */
  setLastProgress(progress: ProgressMessage | null): void;
  /** Forget the last match's ledger. Automatic on match start; exported for the driver and tests. */
  clearProgress(): void;
  /** Back to boot defaults; keeps settings. */
  reset(): void;
}

export type Store = RootState & StoreActions;

// --- shell agent (C-T21) ---------------------------------------------------------------------
// Default equipped emote slots. Ids are the emoji-codepoint frame names of the emotes atlas
// (`public/assets/atlases/emotes.json`), so the scene can resolve a sticker texture straight from
// the id; `shell/emotes.ts` owns the catalogue and `shell/emotes.test.ts` asserts the ids exist.
export const DEFAULT_EMOTE_SLOTS: readonly string[] = [
  '1f525', '1f62d', '1f480', '1f44f', '1f440', '1f624', '1f9e2', '1f451', '1f91d', '1fae1',
];
// --- end shell agent -------------------------------------------------------------------------

export function defaultSession(): SessionSlice {
  return {
    me: null,
    seat: null,
    platform: 'web',
    token: null,
    participants: [],
    speaking: {},
    muted: {},
    connection: 'idle',
    connectionReason: null,
    joinFailure: null,
    lastIntentError: null,
    error: null,
    authNotice: null,
  };
}

export function defaultPresentation(): PresentationSlice {
  return {
    screen: 'boot',
    layoutMode: 'focused',
    orientation: 'landscape',
    perfTier: 0,
    cueBusy: false,
    subtitle: null,
    takeover: null,
    anchors: {},
    displayedScores: [0, 0],
    slamCharge: 0,
    bootProgress: 0,
    hudSafe: null,
    lastHandResult: null,
    lastMatchResult: null,
    announcerVoiceId: null,
    blindNilOffer: null,
    bidLive: false,
    bidConfirm: null,
    emotePickerOpen: false,
    scenePainted: false,
  };
}

export function defaultProgress(): ProgressSlice {
  return { me: null, lastMatch: null, loading: false };
}

export function defaultSettings(): SettingsSlice {
  return {
    volume: { master: 0.8, sfx: 1, music: DEFAULT_MUSIC_VOLUME.web, announcer: 1 },
    audioMuted: false,
    motion: 'system',
    perfOverride: 'auto',
    subtitles: true,
    mutedEmotes: [],
    tutorialDone: false,
    emoteSlots: [...DEFAULT_EMOTE_SLOTS],
  };
}

// --- music agent -------------------------------------------------------------------------------
/**
 * First-run level of the music bed, per surface.
 *
 * Discord is quieter than the web on purpose: the people on that table are usually in a voice
 * call, and a bed at web level competes with them even with the duck doing its job. It is a bed
 * under a conversation there and a soundtrack on the web, so the two start in different places.
 * Either way the Settings slider owns it from the first time a player moves it.
 */
export const DEFAULT_MUSIC_VOLUME: Record<Platform, number> = { web: 0.5, discord: 0.35 };

let musicVolumeWasPersisted = false;

/**
 * Did this session rehydrate a music volume a player had already chosen?
 *
 * The Discord default above may only be applied to a player who has never set one; without this,
 * every Discord reload would stamp 0.35 back over whatever they had picked. Set during `persist`'s
 * merge, which runs once per hydration and not at all when persistence is disabled (tests).
 */
export const hasPersistedMusicVolume = (): boolean => musicVolumeWasPersisted;
// --- end music agent ---------------------------------------------------------------------------

export const SETTINGS_STORAGE_KEY = 'sn.settings';

interface StringStorage {
  getItem(name: string): string | null;
  setItem(name: string, value: string): void;
  removeItem(name: string): void;
}

/**
 * localStorage that never throws: every access is try/caught and falls back to an in-memory
 * map, so `SecurityError` (blocked third-party storage inside an iframe) or quota errors only
 * cost persistence, never boot.
 */
export function safeStorage(get: () => StringStorage | undefined = () => globalThis.localStorage): StringStorage {
  const memory = new Map<string, string>();
  const backing = (): StringStorage | undefined => {
    try {
      return get();
    } catch {
      return undefined;
    }
  };
  return {
    getItem(name) {
      try {
        const v = backing()?.getItem(name);
        if (v != null) return v;
      } catch {
        /* fall through */
      }
      return memory.get(name) ?? null;
    },
    setItem(name, value) {
      memory.set(name, value);
      try {
        backing()?.setItem(name, value);
      } catch {
        /* memory only */
      }
    },
    removeItem(name) {
      memory.delete(name);
      try {
        backing()?.removeItem(name);
      } catch {
        /* memory only */
      }
    },
  };
}

function mergeSettings(persisted: unknown, current: SettingsSlice): SettingsSlice {
  if (!persisted || typeof persisted !== 'object') return current;
  const p = persisted as Partial<SettingsSlice>;
  if (typeof p.volume?.music === 'number') musicVolumeWasPersisted = true;
  return {
    ...current,
    ...p,
    volume: { ...current.volume, ...(p.volume ?? {}) },
    mutedEmotes: Array.isArray(p.mutedEmotes) ? p.mutedEmotes : current.mutedEmotes,
    emoteSlots: Array.isArray(p.emoteSlots) && p.emoteSlots.length > 0 ? p.emoteSlots : current.emoteSlots,
  };
}

export interface CreateStoreOptions {
  /** Storage for `settings`; pass `null` to disable persistence (tests). */
  storage?: StringStorage | null;
}

export function createSpadesStore(opts: CreateStoreOptions = {}) {
  const storage = opts.storage === undefined ? safeStorage() : opts.storage;
  const initial = (): RootState => ({
    session: defaultSession(),
    table: null,
    privateView: null,
    presentation: defaultPresentation(),
    settings: defaultSettings(),
    progress: defaultProgress(),
  });

  return createStore<Store>()(
    subscribeWithSelector(
      persist(
        (set) => ({
          ...initial(),
          setSession: (patch) => set((s) => ({ session: { ...s.session, ...patch } })),
          setSpeaking: (userId, speaking) =>
            set((s) =>
              s.session.speaking[userId] === speaking
                ? s
                : { session: { ...s.session, speaking: { ...s.session.speaking, [userId]: speaking } } },
            ),
          setMuted: (userId, muted) =>
            set((s) =>
              s.session.muted[userId] === muted
                ? s
                : { session: { ...s.session, muted: { ...s.session.muted, [userId]: muted } } },
            ),
          /*
           * `table`, and the one place a new match wipes the last one's XP ledger.
           *
           * There is no `matchStart` cue to hang this on — the server announces a match by moving
           * the phase out of `lobby` (or out of `matchEnd`, on a rematch) — and `progress` is the
           * slice that owns the data, so the rule lives with it rather than in the shell's cue
           * router. Without it, a rematch the server could not pay (records offline, a DB blip)
           * would re-show the PREVIOUS match's ledger on the end screen as if it had just been
           * earned.
           */
          setTable: (table) =>
            set((s) => {
              const idle = (v: TableView | null) => v == null || v.phase === 'lobby' || v.phase === 'matchEnd';
              const starting = idle(s.table) && !idle(table);
              if (!starting || (s.progress.lastMatch == null && !s.progress.loading)) return { table };
              return { table, progress: { ...s.progress, lastMatch: null, loading: false } };
            }),
          setPrivateView: (privateView) => set({ privateView }),
          setPresentation: (patch) => set((s) => ({ presentation: { ...s.presentation, ...patch } })),
          setAnchor: (name, rect) =>
            set((s) => ({ presentation: { ...s.presentation, anchors: { ...s.presentation.anchors, [name]: rect } } })),
          setSettings: (patch) => set((s) => ({ settings: { ...s.settings, ...patch } })),
          setVolume: (patch) =>
            set((s) => ({ settings: { ...s.settings, volume: { ...s.settings.volume, ...patch } } })),
          setProfile: (me, loading = false) => set((s) => ({ progress: { ...s.progress, me, loading } })),
          setLastProgress: (lastMatch) => set((s) => ({ progress: { ...s.progress, lastMatch } })),
          clearProgress: () => set((s) => ({ progress: { ...s.progress, lastMatch: null, loading: false } })),
          reset: () => set((s) => ({ ...initial(), settings: s.settings })),
        }),
        {
          name: SETTINGS_STORAGE_KEY,
          version: 1,
          storage: storage ? createJSONStorage(() => storage) : undefined,
          partialize: (s) => ({ settings: s.settings }),
          merge: (persisted, current) => ({
            ...current,
            settings: mergeSettings((persisted as { settings?: unknown } | undefined)?.settings, current.settings),
          }),
          // With storage=null zustand would still try localStorage; skipHydration keeps it inert.
          skipHydration: storage === null,
        },
      ),
    ),
  );
}

export type SpadesStore = ReturnType<typeof createSpadesStore>;

/** The app singleton. Tests build their own via `createSpadesStore({ storage: null })`. */
export const store: SpadesStore = createSpadesStore();

/** React hook: `useStore((s) => s.presentation.screen)`. */
export function useStore<T>(selector: (state: Store) => T): T {
  return useZustandStore(store, selector);
}
